/**
 * Recovers from Vite "Failed to fetch dynamically imported module" errors
 * (stale chunks after a redeploy, flaky in-app browsers, etc).
 *
 * Strategy:
 *  - First chunk-load failure in a session → set flag and reload once.
 *  - Second failure → escalate to the branded fallback (window.__sayItNowFail).
 *
 * Also unregisters stray service workers that we never installed, which
 * are a common source of "blank page after deploy" in some browsers.
 */
import { getBrowserLabel } from './browserEnv';

const RELOAD_FLAG = 'sin_chunk_reload_v1';

function isChunkLoadError(message: string): boolean {
  if (!message) return false;
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message)
  );
}

function attemptRecovery(reason: string) {
  try {
    const already = sessionStorage.getItem(RELOAD_FLAG);
    if (!already) {
      sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
      // Log for postmortem then hard-reload bypassing cache where possible.
      // eslint-disable-next-line no-console
      console.warn('[ChunkRecovery] reloading once due to:', reason, getBrowserLabel());
      window.location.reload();
      return;
    }
    // Second failure → surface branded fallback if available.
    // eslint-disable-next-line no-console
    console.error('[ChunkRecovery] second failure, showing fallback:', reason);
    const fail = (window as any).__sayItNowFail;
    if (typeof fail === 'function') fail('chunk-load:' + reason);
  } catch {
    /* noop */
  }
}

export function installChunkRecovery() {
  if (typeof window === 'undefined') return;

  // Clear the reload flag once React mounts cleanly so future failures
  // get a fresh single-reload attempt.
  setTimeout(() => {
    try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* noop */ }
  }, 12_000);

  window.addEventListener('error', (event) => {
    const msg = event?.message || event?.error?.message || '';
    if (isChunkLoadError(msg)) attemptRecovery(msg);
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    const reason: any = event?.reason;
    const msg = (reason && (reason.message || String(reason))) || '';
    if (isChunkLoadError(msg)) attemptRecovery(msg);
  });
}

/**
 * Unregister any service worker we never installed. Stale SWs from older
 * deploys (or other sites on the same domain) can keep returning an empty
 * shell and produce a blank page.
 */
export function guardAgainstStaleServiceWorkers() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  // We do not register any SW in this app, so any existing registration is stale.
  navigator.serviceWorker.getRegistrations?.()
    .then((regs) => {
      regs.forEach((r) => {
        // eslint-disable-next-line no-console
        console.warn('[SW Guard] unregistering stale SW:', r.scope);
        r.unregister().catch(() => { /* noop */ });
      });
    })
    .catch(() => { /* noop */ });
}
